import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import Modal from '../components/ui/Modal';
import FormField from '../components/ui/FormField';
import Button from '../components/ui/Button';
import TagChip from '../components/TagChip';
import { Globals } from '../globalfunctions.js';
import roleManager, { PERMISSIONS } from '../utils/roleManager.js';
import { apiClient } from '../utils/apiClient';
import { startImpersonation } from '../utils/impersonation';
import './Users.css';
import { confirmDialog, alertDialog } from '../utils/dialogs';

function Users() {
  const navigate = useNavigate();
  const [sampleUsers, setSampleUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting2FA, setResetting2FA] = useState({});
  const [impersonating, setImpersonating] = useState({});
  const [tags, setTags] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [activeTagIds, setActiveTagIds] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [showManageTags, setShowManageTags] = useState(false);
  const [newTag, setNewTag] = useState({ name: '', colour: '#6c7a89', description: '' });
  const [tagError, setTagError] = useState('');
  const [savingTag, setSavingTag] = useState(false);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    async function fetchUsers() {
      setLoading(true);
      try {
        const data = await apiClient.post('/api/users/findall');
        setSampleUsers(data.map((user) => ({ ...user, _key: user.id || user.user_id })));
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, []);

  // One request for every tag and who has what, rather than a request per
  // row - a customer list is dozens of rows.
  const loadTags = async () => {
    try {
      const data = await apiClient.get('/api/tags/assignments');
      setTags(data.tags);
      setAssignments(data.assignments);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  useEffect(() => { loadTags(); }, []);

  const reset2FA = async (userId, userEmail) => {
    if (!roleManager.hasPermission('reset_user_2fa')) {
      alertDialog('You do not have permission to reset 2FA for users.');
      return;
    }

    const confirmReset = await confirmDialog(
      `Are you sure you want to reset 2FA for ${userEmail}?\n\nThis will:\n- Remove all their 2FA methods\n- Force them to set up 2FA again on next login\n- Log this action for audit purposes`
    );
    if (!confirmReset) return;

    setResetting2FA((prev) => ({ ...prev, [userId]: true }));
    try {
      const result = await apiClient.post('/api/admin/reset-2fa', {
        userId: String(userId),
        adminAction: true,
        reason: '2FA reset by admin'
      });
      alertDialog(`2FA reset successful for ${userEmail}.\n\nThe user will need to set up 2FA again on their next login.\n\nAudit ID: ${result.auditId || 'N/A'}`);
    } catch (error) {
      console.error('2FA reset failed:', error);
      alertDialog(`Failed to reset 2FA: ${error.message}`);
    } finally {
      setResetting2FA((prev) => ({ ...prev, [userId]: false }));
    }
  };

  // The reason is asked for in an on-page dialog. The browser's own prompt()
  // box is blocked or silently cancelled by some iPad/iPhone browsers, which
  // made this button appear to do nothing there.
  const [viewAs, setViewAs] = useState(null); // { user, reason, error }

  const viewAsCustomer = (user) => setViewAs({ user, reason: '', error: '' });

  const confirmViewAs = async (e) => {
    e.preventDefault();
    const { user, reason } = viewAs;
    const userId = user.id || user.user_id;
    if (!reason.trim()) {
      setViewAs((v) => ({ ...v, error: 'A reason is required. It is recorded in the audit log.' }));
      return;
    }
    setImpersonating((prev) => ({ ...prev, [userId]: true }));
    try {
      await startImpersonation(user, reason.trim());
      setViewAs(null);
      navigate('/portal');
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      setViewAs((v) => ({ ...v, error: `Could not view as ${user.email || user.UserName}: ${error.message}` }));
    } finally {
      setImpersonating((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const canImpersonate = roleManager.hasPermission(PERMISSIONS.IMPERSONATE_USERS);
  const canReset2FA = roleManager.hasPermission('reset_user_2fa');
  const canManageTags = roleManager.hasPermission(PERMISSIONS.MANAGE_TAGS);

  const tagById = Object.fromEntries(tags.map((tag) => [tag.id, tag]));
  const tagsForUser = (user) => (assignments[user.id || user.user_id] || []).map((id) => tagById[id]).filter(Boolean);

  const toggleFilter = (tagId) => {
    setActiveTagIds((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]));
  };

  const INACTIVE_STATUSES = ['inactive', 'closed'];
  const isInactive = (user) => INACTIVE_STATUSES.includes(String(user.status || '').trim().toLowerCase());
  const inactiveCount = sampleUsers.filter(isInactive).length;

  // Selecting two tags narrows to customers who have BOTH - segments are
  // for finding "high value AND on legacy hosting", which an OR would bury.
  const visibleUsers = sampleUsers
    .filter((user) => showInactive || !isInactive(user))
    .filter((user) => {
      if (activeTagIds.length === 0) return true;
      const owned = assignments[user.id || user.user_id] || [];
      return activeTagIds.every((id) => owned.includes(id));
    });

  const createTag = async (e) => {
    e.preventDefault();
    if (!newTag.name.trim()) return;
    setSavingTag(true);
    setTagError('');
    try {
      await apiClient.post('/api/tags', newTag);
      setNewTag({ name: '', colour: '#6c7a89', description: '' });
      await loadTags();
    } catch (error) {
      setTagError(error.message || 'Could not create that tag.');
    } finally {
      setSavingTag(false);
    }
  };

  const renameTag = async (tag, name) => {
    if (!name || name === tag.name) return;
    try {
      await apiClient.put(`/api/tags/${tag.id}`, { name });
      await loadTags();
    } catch (error) {
      setTagError(error.message || 'Could not rename that tag.');
    }
  };

  const recolourTag = async (tag, colour) => {
    try {
      await apiClient.put(`/api/tags/${tag.id}`, { colour });
      await loadTags();
    } catch (error) {
      setTagError(error.message || 'Could not update that tag.');
    }
  };

  const deleteTag = async (tag) => {
    if (!await confirmDialog(`Delete the "${tag.name}" tag? It will be removed from every customer that has it.`)) return;
    try {
      await apiClient.delete(`/api/tags/${tag.id}`);
      setActiveTagIds((prev) => prev.filter((id) => id !== tag.id));
      await loadTags();
    } catch (error) {
      setTagError(error.message || 'Could not delete that tag.');
    }
  };

  const columns = [
    { key: 'first_name', label: 'First Name', sortable: true },
    { key: 'last_name', label: 'Last Name', sortable: true },
    { key: 'companyName', label: 'Company Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'phoneNumber', label: 'Phone Number' },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'role_type', label: 'Role', sortable: true },
    {
      key: 'tags',
      label: 'Tags',
      render: (user) => {
        const owned = tagsForUser(user);
        if (owned.length === 0) return <span className="users-no-tags">—</span>;
        return <div className="users-tag-cell">{owned.map((tag) => <TagChip key={tag.id} tag={tag} />)}</div>;
      }
    },
    ...(canReset2FA ? [{
      key: 'twoFactor',
      label: '2FA Management',
      render: (user) => (
        <Button
          variant="danger"
          onClick={(e) => { e.stopPropagation(); reset2FA(user.id || user.user_id, user.email); }}
          disabled={resetting2FA[user.id || user.user_id]}
        >
          {resetting2FA[user.id || user.user_id] ? 'Resetting...' : 'Reset 2FA'}
        </Button>
      )
    }] : []),
    ...(canImpersonate ? [{
      key: 'impersonate',
      label: 'View As',
      render: (user) => (
        String(user.role_type).toLowerCase() === 'customer' ? (
          <Button
            variant="secondary"
            onClick={(e) => { e.stopPropagation(); viewAsCustomer(user); }}
            disabled={impersonating[user.id || user.user_id]}
          >
            {impersonating[user.id || user.user_id] ? 'Loading...' : 'View as Customer'}
          </Button>
        ) : null
      )
    }] : [])
  ];

  return (
    <PageShell>
      <div className="users-header">
        <h1>Users / Customers</h1>
        {canManageTags && (
          <Button variant="secondary" onClick={() => { setTagError(''); setShowManageTags(true); }}>Manage Tags</Button>
        )}
      </div>

      <div className="users-status-filter">
        <label className="users-status-filter-toggle">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive/closed customers
          {!showInactive && inactiveCount > 0 && <span className="users-tag-filter-count">({inactiveCount} hidden)</span>}
        </label>
      </div>

      {tags.length > 0 && (
        <div className="users-tag-filter">
          <span className="users-tag-filter-label">Filter by tag</span>
          {tags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              className={`users-tag-filter-btn ${activeTagIds.includes(tag.id) ? 'is-active' : ''}`}
              onClick={() => toggleFilter(tag.id)}
            >
              <TagChip tag={tag} />
              <span className="users-tag-filter-count">{(assignments && Object.values(assignments).filter((ids) => ids.includes(tag.id)).length) || 0}</span>
            </button>
          ))}
          {activeTagIds.length > 0 && (
            <button type="button" className="users-tag-filter-clear" onClick={() => setActiveTagIds([])}>Clear</button>
          )}
        </div>
      )}

      {loading ? (
        <p>Loading users...</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={visibleUsers}
          rowKey="_key"
          onRowClick={(user) => navigate(`/customer/${user.id || user.user_id}`)}
          emptyMessage={activeTagIds.length > 0 ? 'No customers carry all of those tags.' : 'No users found.'}
        />
      )}

      <Modal
        open={!!viewAs}
        onClose={() => setViewAs(null)}
        title="View as customer"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setViewAs(null)}>Cancel</Button>
            <Button type="submit" form="view-as-form" disabled={viewAs ? !!impersonating[viewAs.user.id || viewAs.user.user_id] : false}>
              {viewAs && impersonating[viewAs.user.id || viewAs.user.user_id] ? 'Loading...' : 'View as customer'}
            </Button>
          </>
        )}
      >
        {viewAs && (
          <form id="view-as-form" onSubmit={confirmViewAs}>
            <p>You will see the portal exactly as {viewAs.user.email || viewAs.user.UserName} does, read-only, for 30 minutes.</p>
            <FormField id="view-as-reason" label="Reason (recorded in the audit log)">
              <input
                id="view-as-reason"
                type="text"
                autoFocus
                value={viewAs.reason}
                onChange={(e) => setViewAs((v) => ({ ...v, reason: e.target.value, error: '' }))}
                placeholder="e.g. Checking the invoice they asked about"
              />
            </FormField>
            {viewAs.error && <p role="alert" style={{ color: '#c0392b', margin: '8px 0 0' }}>{viewAs.error}</p>}
          </form>
        )}
      </Modal>

      <Modal
        open={showManageTags}
        onClose={() => setShowManageTags(false)}
        title="Manage Tags"
        footer={<Button variant="secondary" onClick={() => setShowManageTags(false)}>Done</Button>}
      >
        <div className="users-tag-manage">
          {tags.length === 0 && <p className="users-no-tags">No tags yet.</p>}
          {tags.map((tag) => (
            <div key={tag.id} className="users-tag-row">
              <input
                type="color"
                value={tag.colour}
                onChange={(e) => recolourTag(tag, e.target.value)}
                aria-label={`Colour for ${tag.name}`}
              />
              <input
                type="text"
                defaultValue={tag.name}
                onBlur={(e) => renameTag(tag, e.target.value.trim())}
                aria-label={`Name for ${tag.name}`}
              />
              <span className="users-tag-row-count">
                {Object.values(assignments).filter((ids) => ids.includes(tag.id)).length}
              </span>
              <Button variant="danger" onClick={() => deleteTag(tag)}>Delete</Button>
            </div>
          ))}

          <form className="users-tag-new" onSubmit={createTag}>
            <FormField id="new-tag-name" label="New tag">
              <div className="users-tag-new-row">
                <input type="color" value={newTag.colour} onChange={(e) => setNewTag({ ...newTag, colour: e.target.value })} aria-label="New tag colour" />
                <input
                  id="new-tag-name"
                  value={newTag.name}
                  onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                  placeholder="e.g. High value"
                />
                <Button type="submit" disabled={savingTag || !newTag.name.trim()}>Add</Button>
              </div>
            </FormField>
          </form>

          {tagError && <div className="users-tag-error">{tagError}</div>}
        </div>
      </Modal>
    </PageShell>
  );
}

export default Users;
